import React from 'react';
import ReactDOM from 'react-dom';
import { STICKY_ROOT } from 'consts/rootNodes';
import styled from '@emotion/styled';
import CloseIcon from 'assets/svg/close_1.5.svg';
import SuccessIcon from 'assets/svg/checkmark-tooltip.svg';
import ArrowIcon from 'assets/svg/arrow-left.svg';
import { SmallText, Text } from 'ds/components/typography';
import { useLocale } from 'locale';
import { connect } from 'react-redux';
import { RootAction, State } from 'store/state';
import { activePromotionPlanAutoUpdatePeriodSelector, isBasicPromotionSelector } from 'store/state/app/private-user-monetization/selectors';
import { Dispatch } from 'redux';
import { upgradeUserPlanFlow } from 'store/state/app/actions';
import { minutesToHours } from 'helpers/timeUtils';
import { PrivateUserMonetizationSource } from 'store/state/app';


const Wrapper = styled.div`
  position: fixed;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.brand.main};
  padding: ${({ theme }) => theme.spacing(2)};
  z-index: 1001;
  color: ${({ theme }) => theme.colors.neutrals.white};
  display: flex;
  justify-content: space-between;
  ${({ theme }) => theme.media.showAt({ from: 2 })`
    inset: auto 100px 50px auto;
    width: 300px;
  `}
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    inset: auto 16px 16px;
  `}
  svg {
    path {
      fill: ${({ theme }) => theme.colors.neutrals.white};
    }
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const MainMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const LinkMessage = styled(SmallText)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  text-transform: uppercase;
  cursor: pointer;
`;

const SuccessWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  border: 1px solid ${({ theme }) => theme.colors.neutrals.white};
  width: 24px;
  height: 24px;
  line-height: 0;
  gap: ${({ theme }) => theme.spacing(1)};
  text-transform: uppercase;
  overflow: hidden;
`;

const TextWrapper = styled(Text)`
  opacity: 0.8;
`;

interface ISuccessMessage {
  onClose: () => void;
  onUpgradePromotionPlanClick: () => void;
  isBasic: boolean;
  autoUpdatePeriod: number;
}

const SuccessMessageBase: React.FC<ISuccessMessage> = ({ onClose, isBasic, onUpgradePromotionPlanClick, autoUpdatePeriod }) => {
  const { t } = useLocale();

  const handleClick = () => {
    onUpgradePromotionPlanClick();
    onClose();
  };

  if (isBasic && !autoUpdatePeriod) return null;

  return ReactDOM.createPortal(
      <>
        <Wrapper>
          <ContentWrapper>
            <MainMessage>
              <SuccessWrapper>
                <SuccessIcon width={16} height={16} />
              </SuccessWrapper>
              <TextWrapper>{t('privateBulletinPromotion.successfullyPromoted')}</TextWrapper>
            </MainMessage>
            {isBasic ? (
              <LinkMessage weight="bold" onClick={handleClick}>
                {t('privateBulletinPromotion.successMessageLink', { hours: minutesToHours(autoUpdatePeriod) })}
                <ArrowIcon width={16} height={16} />
              </LinkMessage>
            ) : null}
          </ContentWrapper>
          <CloseIcon onClick={onClose} />
        </Wrapper>
      </>,
      document.getElementById(STICKY_ROOT)
    );
};

const mapStateToProps = (state: State) => ({
  isBasic: isBasicPromotionSelector(state),
  autoUpdatePeriod: activePromotionPlanAutoUpdatePeriodSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onUpgradePromotionPlanClick: () => dispatch(upgradeUserPlanFlow({ source: PrivateUserMonetizationSource.ListingSuccessPromotedMessage, userInitiated: true })),
});

export const SuccessMessage = connect(mapStateToProps, mapDispatchToProps)(SuccessMessageBase);
